// @ts-nocheck
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Link as LinkIcon,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Stack,
  Button,
  Divider,
  Typography,
  MenuItem,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { Menu } from 'react-contexify';
import { useDropzone } from 'react-dropzone';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

import {
  NOT_AUTHORIZED_TO_ACCESS_MESSAGE,
  MAX_IMAGE_SIZE,
} from '../../../../const/CommonConst';
import { ReactComponent as SendIcon } from '../../../../assets/Icons/ChatIcons/send.svg';
import { ReactComponent as AddIcon } from '../../../../assets/Icons/QueryTracker/AddIcon.svg';
import swalToast from '../../swalToast';

const BLOCK_TYPES = [
  {
    label: 'Normal text',
    cmd: (editor) => editor.chain().focus().setParagraph().run(),
    isActive: (editor) =>
      editor.isActive('paragraph') && !editor.isActive('heading'),
  },
  {
    label: 'Heading 1',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
  },
  {
    label: 'Heading 2',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
  },
  {
    label: 'Heading 3',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 3 }),
  },
  {
    label: 'Heading 4',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 4 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 4 }),
  },
  {
    label: 'Heading 5',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 5 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 5 }),
  },
  {
    label: 'Heading 6',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 6 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 6 }),
  },
];

/**
 * @component ToolbarMenuItem
 * @description Renders a block-format option and forwards its selected type to the toolbar.
 * @returns {JSX.Element} The block-format menu item.
 */
const ToolbarMenuItem = ({ type, handleTextTypeChange }) => {
  const onClick = useCallback(
    (e) => handleTextTypeChange(e, type),
    [type, handleTextTypeChange]
  );
  return (
    <MenuItem
      disableRipple
      value={type.label}
      onClick={onClick}
      key={type.label}
    >
      <Typography>{type.label}</Typography>
    </MenuItem>
  );
};

ToolbarMenuItem.propTypes = {
  type: PropTypes.shape({
    label: PropTypes.string.isRequired,
  }).isRequired,
  handleTextTypeChange: PropTypes.func.isRequired,
};

/**
 * @component EditorToolBar
 * @description Provides TipTap formatting, image and attachment actions, optional hyperlink insertion, and mode-specific submission controls.
 * @returns {JSX.Element} The editor toolbar.
 * @example <EditorToolBar editor={editor} enableHyperlinks handleAddLink={handleAddLink} />
 */
const EditorToolBar = ({
  editor,
  handleAttachFile,
  isEditorEmpty,
  handleSubmit,
  mode = 'default',
  cancelButton = null,
  submitButton = null,
  isUserAuthorizedForAddNote,
  showAttachment = false,
  enableHyperlinks = true,
  handleAddLink,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [textType, setTextType] = useState(BLOCK_TYPES[0]);
  const imageInputRef = useRef(null);
  const theme = useTheme();
  const open = Boolean(anchorEl);

  const {
    acceptedFiles: identifyingDocumentAcceptedFiles,
    fileRejections: identifyingDocumentRejectedFiles,
    getRootProps: addAttachmentsGetRootProps,
    getInputProps: addAttachmentsGetInputProps,
    open: openAddAttachments,
  } = useDropzone({
    accept: {
      'application/pdf': [],
      'text/plain': [],
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
    },
  });

  const handleClick = useCallback(
    (event) => setAnchorEl(event?.currentTarget),
    []
  );
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleImageIconClick = useCallback(() => {
    if (imageInputRef.current) imageInputRef.current.click();
  }, []);

  useEffect(() => {
    if (identifyingDocumentRejectedFiles?.length > 0) {
      swalToast({
        icon: 'error',
        title:
          'Please upload a document with a valid format. Accepted formats include .pdf, .xlsx, .xls, .txt',
      });
      return;
    }
    if (identifyingDocumentAcceptedFiles?.length > 0) {
      handleAttachFile(identifyingDocumentAcceptedFiles);
    }
  }, [identifyingDocumentAcceptedFiles, identifyingDocumentRejectedFiles]);

  // Sync toolbar text type indicator with editor state
  useEffect(() => {
    if (!editor) return;
    const active = BLOCK_TYPES.find((t) => t.isActive(editor));
    if (active && active.label !== textType.label) {
      setTextType(active);
    }
  }, [editor?.state]);

  const handleTextTypeChange = useCallback(
    (e, selectedType) => {
      if (!editor) return;
      setTextType(selectedType);
      selectedType.cmd(editor);
      handleClose();
    },
    [editor, handleClose]
  );

  const handleImageUpload = useCallback(
    (event) => {
      const file = event?.target?.files?.[0];
      if (!file || !editor) {
        return;
      }

      if (file?.size > MAX_IMAGE_SIZE) {
        swalToast({
          icon: 'error',
          title: 'Image size should not exceed 5MB.',
        });
        const input = event?.target;
        if (input) {
          input.value = '';
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 1200;
          let { width, height } = img;
          if (width > height && width > maxSize) {
            height *= maxSize / width;
            width = maxSize;
          } else if (height > maxSize) {
            width *= maxSize / height;
            height = maxSize;
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          const resizedBase64 = canvas.toDataURL('image/jpeg', 0.9);
          editor.chain().focus().setImage({ src: resizedBase64 }).run();
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
      // reset input
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    },
    [editor]
  );

  const getIconButtonSx = (isActive) => ({
    bgcolor: isActive ? theme.palette.action.selected : 'transparent',
    '&:hover': {
      bgcolor: isActive
        ? theme.palette.action.selected
        : theme.palette.action.hover,
    },
  });

  const handleBoldMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      editor.chain().focus().toggleBold().run();
    },
    [editor]
  );

  const handleItalicMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      editor.chain().focus().toggleItalic().run();
    },
    [editor]
  );

  const handleUnderlineMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      editor.chain().focus().toggleUnderline().run();
    },
    [editor]
  );

  const handleBulletListMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      editor.chain().focus().toggleBulletList().run();
    },
    [editor]
  );

  const handleOrderedListMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      editor.chain().focus().toggleOrderedList().run();
    },
    [editor]
  );

  /**
   * @function handleLinkMouseDown
   * @description Prevents the toolbar button from taking focus so the editor selection remains available to the link dialog.
   */
  const handleLinkMouseDown = useCallback((event) => {
    event.preventDefault();
  }, []);

  const renderActionButtons = () => {
    if (mode === 'lifecycle') return null;
    if (mode === 'Edit') {
      return (
        <Box
          sx={{ flex: 'auto', justifyContent: 'end', paddingTop: '2px' }}
          display="flex"
        >
          <Box
            variant="contained"
            disabled={isEditorEmpty}
            sx={{
              fontSize: '10px',
              height: 1,
              minWidth: 2,
              fontWeight: 700,
              cursor: isEditorEmpty ? 'default' : 'pointer',
            }}
            title="Cancel"
          >
            {cancelButton}
          </Box>
          <Box
            size="small"
            variant="contained"
            disabled={isEditorEmpty}
            sx={{
              fontSize: '10px',
              height: 1,
              minWidth: 2,
              fontWeight: 700,
              cursor: isEditorEmpty ? 'default' : 'pointer',
            }}
            title="Submit"
          >
            {submitButton}
          </Box>
        </Box>
      );
    }

    let buttonTitle = 'Send';
    if (!isUserAuthorizedForAddNote) {
      buttonTitle = NOT_AUTHORIZED_TO_ACCESS_MESSAGE;
    } else if (isEditorEmpty) {
      buttonTitle = 'Add a message before submitting.';
    }

    return (
      <IconButton
        size="small"
        variant="contained"
        color="primary"
        disabled={isEditorEmpty || !isUserAuthorizedForAddNote}
        onClick={handleSubmit}
        sx={{
          fontSize: '10px',
          height: 1,
          minWidth: 2,
          fontWeight: 700,
          ml: 'auto',
          cursor: isEditorEmpty ? 'default' : 'pointer',
          alignSelf: 'center',
        }}
        title={buttonTitle}
        aria-label={buttonTitle}
      >
        <SendIcon />
      </IconButton>
    );
  };

  if (!editor) return null;

  return (
    <Box className="editor-toolbar">
      <Stack direction="row" flexWrap="wrap" flexGrow={1} alignItems="center">
        <Button
          sx={{ flex: '0 0 auto' }}
          label="text-styles"
          id="text-type-select"
          aria-controls={open ? 'text-type-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          {textType.label || 'Text Type'}
        </Button>
        <Menu
          id="text-type-menu"
          aria-labelledby="text-type-select"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {BLOCK_TYPES.map((type) => (
            <ToolbarMenuItem
              key={type.label}
              type={type}
              handleTextTypeChange={handleTextTypeChange}
            />
          ))}
        </Menu>
        <Divider orientation="vertical" flexItem variant="middle" />
        <IconButton
          color="inherit"
          size="small"
          title="Bold"
          onMouseDown={handleBoldMouseDown}
          sx={getIconButtonSx(editor.isActive('bold'))}
        >
          <FormatBold color={editor.isActive('bold') ? 'primary' : 'inherit'} />
        </IconButton>
        <IconButton
          color="inherit"
          size="small"
          title="Italic"
          onMouseDown={handleItalicMouseDown}
          sx={getIconButtonSx(editor.isActive('italic'))}
        >
          <FormatItalic
            color={editor.isActive('italic') ? 'primary' : 'inherit'}
          />
        </IconButton>
        <IconButton
          color="inherit"
          size="small"
          title="Underline"
          onMouseDown={handleUnderlineMouseDown}
          sx={getIconButtonSx(editor.isActive('underline'))}
        >
          <FormatUnderlined
            color={editor.isActive('underline') ? 'primary' : 'inherit'}
          />
        </IconButton>
        <Divider orientation="vertical" flexItem variant="middle" />
        <IconButton
          color="inherit"
          size="small"
          title="Bullet list"
          onMouseDown={handleBulletListMouseDown}
          sx={getIconButtonSx(editor.isActive('bulletList'))}
        >
          <FormatListBulletedIcon
            color={editor.isActive('bulletList') ? 'primary' : 'inherit'}
          />
        </IconButton>
        <IconButton
          color="inherit"
          size="small"
          title="Numbered list"
          onMouseDown={handleOrderedListMouseDown}
          sx={getIconButtonSx(editor.isActive('orderedList'))}
        >
          <FormatListNumberedIcon
            color={editor.isActive('orderedList') ? 'primary' : 'inherit'}
          />
        </IconButton>
        <IconButton
          color="inherit"
          size="small"
          title="Insert image"
          onClick={handleImageIconClick}
        >
          <ImageIcon />
        </IconButton>
        <input
          type="file"
          accept="image/*"
          ref={imageInputRef}
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />
        {enableHyperlinks && (
          <IconButton
            color="inherit"
            size="small"
            title="Insert hyperlink"
            aria-label="Insert hyperlink"
            onMouseDown={handleLinkMouseDown}
            onClick={handleAddLink}
            disabled={
              !editor.state.selection.$from.sameParent(
                editor.state.selection.$to
              )
            }
          >
            <LinkIcon />
          </IconButton>
        )}
        {showAttachment && (
          <>
            <Divider orientation="vertical" flexItem variant="middle" />
            <IconButton
              color="inherit"
              size="small"
              title="Add attachments"
              {...addAttachmentsGetRootProps()}
              onClick={openAddAttachments}
            >
              <AddIcon />
              <input
                style={{ display: 'none' }}
                {...addAttachmentsGetInputProps()}
              />
            </IconButton>
          </>
        )}
      </Stack>
      {renderActionButtons()}
    </Box>
  );
};

EditorToolBar.propTypes = {
  editor: PropTypes.oneOfType([PropTypes.object]),
  handleAttachFile: PropTypes.func,
  isEditorEmpty: PropTypes.bool,
  handleSubmit: PropTypes.func,
  mode: PropTypes.string,
  cancelButton: PropTypes.node,
  submitButton: PropTypes.node,
  isUserAuthorizedForAddNote: PropTypes.bool,
  showAttachment: PropTypes.bool,
  enableHyperlinks: PropTypes.bool,
  handleAddLink: PropTypes.func,
};

export default EditorToolBar;
